<script setup>
import { computed, ref, watch } from 'vue'

// Collapsible sections state
const expandedSections = ref({
  additionalFilters: true,
  seasons: true,
  leagues: true
})

// Full height state for leagues section
const leaguesFullHeight = ref(false)

function toggleSection(section) {
  expandedSections.value[section] = !expandedSections.value[section]
}

function toggleLeaguesFullHeight() {
  leaguesFullHeight.value = !leaguesFullHeight.value
}

const props = defineProps({
  seasons: {
    type: Array,
    required: true
  },
  selectedSeason: {
    type: Array,
    default: () => []
  },
  selectedLeagues: {
    type: Array,
    default: () => []
  },
  availableLeagues: {
    type: Array,
    default: () => []
  },
  selectedAdditionalFilter: {
    type: String,
    default: 'allGames'
  }
})

const emit = defineEmits(['update:selectedSeason', 'update:selectedLeagues', 'update:additionalFilter'])

// Search functionality
const leagueSearch = ref('')

const safeSelectedLeagues = computed(() => Array.isArray(props.selectedLeagues) ? props.selectedLeagues : [])
const safeSelectedSeason = computed(() => Array.isArray(props.selectedSeason) ? props.selectedSeason : [])

// Filtered leagues based on search
const filteredLeagues = computed(() => {
  if (!leagueSearch.value.trim()) {
    return props.availableLeagues
  }
  
  const searchTerm = leagueSearch.value.toLowerCase().trim()
  return props.availableLeagues.filter(league => 
    league.name.toLowerCase().includes(searchTerm) ||
    league.id.toLowerCase().includes(searchTerm)
  )
})

// Debug log to see what's being passed
console.log('=== GlobalParametersSidebar received ===')
console.log('selectedSeason:', props.selectedSeason)
console.log('selectedLeagues:', props.selectedLeagues)
console.log('selectedLeagues type:', typeof props.selectedLeagues)
console.log('selectedLeagues length:', props.selectedLeagues?.length)
console.log('safeSelectedSeason:', safeSelectedSeason.value)
console.log('safeSelectedLeagues:', safeSelectedLeagues.value)
console.log('availableLeagues count:', props.availableLeagues?.length)

function toggleSeason(season) {
  let newSeasons = [...safeSelectedSeason.value]
  const idx = newSeasons.indexOf(season)
  if (idx === -1) {
    newSeasons.push(season)
  } else {
    newSeasons.splice(idx, 1)
  }
  console.log('Toggling season:', season, 'New seasons:', newSeasons)
  emit('update:selectedSeason', newSeasons)
}

function toggleLeague(leagueId) {
  let newLeagues = [...safeSelectedLeagues.value]
  const idx = newLeagues.indexOf(leagueId)
  if (idx === -1) {
    newLeagues.push(leagueId)
  } else {
    newLeagues.splice(idx, 1)
  }
  console.log('Toggling league:', leagueId, 'New leagues:', newLeagues)
  emit('update:selectedLeagues', newLeagues)
}

function toggleAllLeagues() {
  const allLeagueIds = props.availableLeagues.map(league => league.id)
  
  // Check if all leagues are currently selected by comparing sets
  const selectedSet = new Set(safeSelectedLeagues.value)
  const allSet = new Set(allLeagueIds)
  const allSelected = allLeagueIds.length > 0 && allLeagueIds.every(id => selectedSet.has(id))
  
  if (allSelected) {
    // If all are selected, deselect all
    console.log('Deselecting all leagues')
    emit('update:selectedLeagues', [])
  } else {
    // If not all are selected, select all
    console.log('Selecting all leagues')
    emit('update:selectedLeagues', allLeagueIds)
  }
}

// Check if all leagues are selected
const allLeaguesSelected = computed(() => {
  const allLeagueIds = props.availableLeagues.map(league => league.id)
  if (allLeagueIds.length === 0) return false
  
  // Check if all available leagues are in the selected list
  return allLeagueIds.every(id => safeSelectedLeagues.value.includes(id))
})

const selectedAdditionalFilter = ref(props.selectedAdditionalFilter)

// Watch for changes in additional filter and emit updates
watch(selectedAdditionalFilter, (newFilter) => {
  console.log('Additional filter changed to:', newFilter)
  emit('update:additionalFilter', newFilter)
}, { immediate: true })

// Watch for changes in the prop and update local state
watch(() => props.selectedAdditionalFilter, (newFilter) => {
  console.log('Additional filter prop changed to:', newFilter)
  selectedAdditionalFilter.value = newFilter
}, { immediate: true })

// Watch for changes in selectedLeagues prop
watch(() => props.selectedLeagues, (newLeagues) => {
  console.log('=== selectedLeagues prop changed ===')
  console.log('New leagues:', newLeagues)
  console.log('Type:', typeof newLeagues)
  console.log('Length:', newLeagues?.length)
}, { immediate: true, deep: true })
</script>

<template>
  <aside class="bg-white text-gray-700 w-[280px] min-h-full p-4 rounded-l-lg flex flex-col gap-4 border-r border-gray-200">
    
    <!-- Global Parameters Header -->
    <div class="flex-shrink-0">
      <div class="font-semibold text-lg mb-4 text-pink-600">Global Parameters</div>
      <div class="text-sm text-gray-500 mb-4">These settings will apply to all players globally</div>
    </div>

    <!-- Additional Filters Section -->
    <div v-show="!leaguesFullHeight" class="flex-shrink-0">
      <div class="flex items-center justify-between mb-4 cursor-pointer" @click="toggleSection('additionalFilters')">
        <div class="font-semibold text-lg">Additional Filters</div>
        <i class="fas transition-transform duration-200" :class="expandedSections.additionalFilters ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
      <div v-show="expandedSections.additionalFilters" class="flex flex-col gap-2"> 
        <label
          v-for="(label, key) in {
            allGames: 'All Games',
            lastFiveGames: 'Last 5 Games',
            withoutLast5Games: 'Rest of Season (Without Last 5)'
          }"
          :key="key"
          class="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1"
        >
          <input
            type="radio"
            class="form-radio h-4 w-4 text-pink-500 border-pink-400 focus:ring-0"
            :value="key"
            v-model="selectedAdditionalFilter"
          />
          <span class="text-gray-700">{{ label }}</span>
        </label>
      </div>
    </div>

    <!-- Seasons Section -->
    <div v-show="!leaguesFullHeight" class="flex-shrink-0">
      <div class="flex items-center justify-between mb-4 cursor-pointer" @click="toggleSection('seasons')">
        <div class="font-semibold text-lg">Seasons</div>
        <i class="fas transition-transform duration-200" :class="expandedSections.seasons ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
      <div v-show="expandedSections.seasons" class="flex flex-col gap-2">
        <label v-for="season in seasons" :key="season" class="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1"
          :class="safeSelectedSeason.includes(season) ? 'bg-pink-50' : ''">
          <input type="checkbox"
                 :checked="safeSelectedSeason.includes(season)"
                 @change="() => toggleSeason(season)"
                 class="form-checkbox h-4 w-4 text-pink-500 border-pink-400 focus:ring-0" />
          <span :class="safeSelectedSeason.includes(season) ? 'font-bold text-pink-500' : 'text-gray-700'">{{ season }}</span>
        </label>
      </div>
    </div>
    
    <!-- Leagues Section -->
    <div class="flex-1 flex flex-col min-h-0">
      <div class="flex items-center justify-between mb-4 flex-shrink-0">
        <div class="flex items-center gap-2">
          <div class="cursor-pointer" @click="toggleSection('leagues')">
            <div class="font-semibold text-lg">Leagues</div>
          </div>
          <button
            @click="toggleLeaguesFullHeight"
            class="px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded border border-blue-200 hover:border-blue-300 transition-colors"
            :title="leaguesFullHeight ? 'Normal Height' : 'Full Sidebar Height'"
          >
            <i class="fas" :class="leaguesFullHeight ? 'fa-compress' : 'fa-expand'"></i>
          </button>
        </div>
        <i class="fas transition-transform duration-200 cursor-pointer" :class="expandedSections.leagues ? 'fa-chevron-up' : 'fa-chevron-down'" @click="toggleSection('leagues')"></i>
      </div>
      
      <div v-show="expandedSections.leagues" class="flex-1 flex flex-col min-h-0">
        <!-- Toggle All Button -->
        <div class="flex-shrink-0 mb-3">
          <button
            @click="toggleAllLeagues"
            class="w-full px-3 py-2 text-sm font-medium text-pink-600 hover:bg-pink-50 rounded-md border border-pink-200 hover:border-pink-300 transition-colors flex items-center justify-center gap-2"
          >
            <i class="fas" :class="allLeaguesSelected ? 'fa-check-square' : 'fa-square'"></i>
            {{ allLeaguesSelected ? 'Deselect All' : 'Select All' }}
          </button>
        </div>
        
        <!-- Search Input -->
        <div class="flex-shrink-0 mb-3">
          <input 
            v-model="leagueSearch"
            type="text"
            placeholder="Search leagues..."
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>
        
        <div class="flex flex-col gap-2 flex-1 overflow-y-auto">
          <label v-for="league in filteredLeagues" :key="league.id" class="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1 flex-shrink-0"
            :class="safeSelectedLeagues.includes(league.id) ? 'bg-pink-50' : ''">
            <input type="checkbox"
                   :checked="safeSelectedLeagues.includes(league.id)"
                   @change="() => toggleLeague(league.id)"
                   class="form-checkbox h-4 w-4 text-pink-500 border-pink-400 focus:ring-0" />
            <span :class="safeSelectedLeagues.includes(league.id) ? 'font-bold text-pink-500 text-[12px]' : 'text-gray-700 text-[12px]'">{{ league.name }}</span>
          </label>
          
          <!-- No results message -->
          <div v-if="filteredLeagues.length === 0 && leagueSearch.trim()" class="text-center text-gray-500 text-sm py-4">
            No leagues found matching "{{ leagueSearch }}"
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>
